import React from 'react';
import { Spinner } from 'react-bootstrap';
import { HistoryRow } from '~/components/Patient/HealthHx';
import { Select } from '~/components/Select';
import { ServicesContext, useStoreMeasureAssociation } from '~/services';
import {
    HealthHxItemType,
    healthHxFilter,
    healthHxSort,
    healthHxTabs,
    sortOptions,
    useDebounce,
} from '~/util';

interface HealthHxProps {
    show: boolean;
    patientId: number;
    setTabValue: (x: number | string) => void;
    setHealthHxVal: (x: number | string) => void;
}
export function HealthHx({ show, patientId, setTabValue, setHealthHxVal }: HealthHxProps) {
    const servicesContext = React.useContext(ServicesContext);
    const storeMeasureAssociations = useStoreMeasureAssociation();
    const tabs = React.useMemo(
        () => [
            healthHxTabs.labs,
            healthHxTabs.meds,
            healthHxTabs.specVisits,
            healthHxTabs.diagnosis,
            healthHxTabs.vitals,
            ...(servicesContext.user.hasFeatureToggle('inpt_outpt')
                ? [healthHxTabs.inptOutpt]
                : []),
        ],
        [servicesContext.user]
    );
    const [tab, setTab] = React.useState(tabs[0]);
    const [historyLists, setHistoryLists] = React.useState<{ [key: string]: HealthHxItemType[] }>(
        {}
    );
    const [filteredHistoryLists, setFilteredHistoryLists] = React.useState<{
        [key: string]: HealthHxItemType[];
    }>({});
    const [hasError, setHasError] = React.useState<{ [key: string]: boolean }>({});
    const [sortValue, setSortValue] = React.useState<{
        [key: string]: { value: string; label: string };
    }>(tabs.reduce((cur, x) => ({ ...cur, [x]: sortOptions[x][0] }), {}));
    const [filterValues, setFilterValues] = React.useState<{ [key: string]: string }>(
        tabs.reduce((cur, x) => ({ ...cur, [x]: '' }), {})
    );
    const [debouncedFilterValues, setDebouncedFilterValues] = React.useState<{
        [key: string]: string;
    }>(tabs.reduce((cur, x) => ({ ...cur, [x]: '' }), {}));

    const debounceFilterChange = useDebounce((value) => {
        setDebouncedFilterValues((prev) => ({ ...prev, [tab]: value }));
    }, 250);

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setFilterValues((prev) => ({ ...prev, [tab]: value }));
        debounceFilterChange(value);
    };

    const getTabTitle = (hxTab: string) => {
        if (hxTab === healthHxTabs.meds) return 'Medications';
        if (hxTab === healthHxTabs.specVisits) return 'Specialty Visits';
        if (hxTab === healthHxTabs.inptOutpt) return 'Inpatient / Outpatient Visits';
        return hxTab;
    };

    React.useEffect(() => {
        (async () => {
            setTabValue('pending');
            setHealthHxVal('pending');
            let counter = 0;
            await Promise.all(
                tabs.map(async (x) => {
                    try {
                        const result = await servicesContext.patient.getHealthHistory(patientId, x);
                        setHistoryLists((prev) => ({ ...prev, [x]: result }));
                        counter += result.length;
                        if (x === healthHxTabs.meds) {
                            storeMeasureAssociations(result);
                        }
                    } catch (e) {
                        setHasError((prev) => ({ ...prev, [x]: true }));
                    }
                })
            );
            setTabValue(counter);
            setHealthHxVal(counter);
        })();
    }, [
        servicesContext.patient,
        patientId,
        setTabValue,
        setHealthHxVal,
        tabs,
        storeMeasureAssociations,
    ]);

    React.useEffect(() => {
        if (historyLists[tab]) {
            setFilteredHistoryLists((prev) => ({
                ...prev,
                [tab]: healthHxFilter(
                    historyLists[tab],
                    debouncedFilterValues[tab].toLowerCase(),
                    tab
                ),
            }));
        }
    }, [historyLists, debouncedFilterValues, tab]);

    if (!show) return null;
    return (
        <div id="healthhx">
            <div className="row align-items-center">
                <div className="col-12 col-md-12 col-lg-7 mb-3">
                    <div className="btn-group">
                        {tabs.map((x) => (
                            <button
                                key={x}
                                onClick={() => setTab(x)}
                                type="button"
                                className={`btn ${tab === x ? 'btn-primary' : 'btn-light'}`}
                                title={getTabTitle(x)}
                            >
                                {x === healthHxTabs.diagnosis ? 'Diagnoses' : x} (
                                {historyLists?.[x] ? historyLists[x].length : '-'})
                            </button>
                        ))}
                    </div>
                </div>
                <div className="col-12 col-md-6 col-lg-2 mb-3">
                    {[
                        healthHxTabs.labs,
                        healthHxTabs.meds,
                        healthHxTabs.specVisits,
                        healthHxTabs.diagnosis,
                        healthHxTabs.inptOutpt,
                    ].includes(tab) && (
                        <input
                            className="form-control"
                            onChange={handleFilterChange}
                            value={filterValues[tab]}
                            placeholder={`Filter ${tab}`}
                            autoComplete="off"
                        />
                    )}
                </div>
                <div className="col-12 col-md-6 col-lg-3 mb-3">
                    <Select
                        id="health-hx-sort"
                        label="sort"
                        options={sortOptions[tab]}
                        value={sortValue[tab]}
                        onChange={(val: { value: string; label: string }) =>
                            setSortValue((prev) => ({ ...prev, [tab]: val }))
                        }
                        formatOptionLabel={({ label }, { context }) => {
                            if (context === 'value') {
                                return `Sort by: ${label}`;
                            }
                            return label;
                        }}
                    />
                </div>
            </div>
            <div className="note-scroll">
                {!historyLists?.[tab] && !hasError?.[tab] && (
                    <div className="mt-3 text-center">
                        <Spinner animation="border" />
                    </div>
                )}
                {historyLists?.[tab]?.length === 0 && (
                    <div className="mt-2 text-center h5">
                        There is no {tab} data available for this patient.
                    </div>
                )}
                {historyLists?.[tab]?.length > 0 && filteredHistoryLists?.[tab]?.length === 0 && (
                    <div className="mt-2 text-center h5">No matches found.</div>
                )}
                {hasError?.[tab] && (
                    <div className="mt-3 mx-5 text-center">
                        <p>Apologies, the requested data could not be retrieved at this time.</p>
                        <p>
                            Please try again later. If the issue persists, please contact the
                            support team at{' '}
                            <a href="mailto:HelpDesk@wellmed.net">HelpDesk@wellmed.net</a> or
                            1-877-435-7576.
                        </p>
                    </div>
                )}
                {filteredHistoryLists?.[tab]?.length > 0 &&
                    filteredHistoryLists[tab]
                        .sort((a, b) => healthHxSort(sortValue[tab].value, a, b))
                        .map((x) => <HistoryRow history={x} type={tab} key={x.id} />)}
            </div>
        </div>
    );
}
